// The Higgsfield project picker (route A), as pure state: which projects are ticked, grouped per Higgsfield workspace.
// A plan comes from one Higgsfield workspace (its elements are that workspace's), so ticking is per workspace: while
// one workspace has ticks, the others wait until they are cleared. No React, no API: the dialog renders this.
import type { HiggsfieldWorkspace } from "../../data/higgsfield-api.ts";

export interface PickerState {
  /** the Higgsfield workspace the ticks belong to; null when nothing is ticked */
  workspaceId: string | null;
  /** ticked project ids, in the order the workspace lists them */
  selected: readonly string[];
}

export const EMPTY_PICK: PickerState = { workspaceId: null, selected: [] };

export type GroupState = "all" | "some" | "none" | "waiting";

const listed = (workspaces: readonly HiggsfieldWorkspace[], wsId: string) =>
  workspaces.find((w) => w.id === wsId)?.projects.map((p) => p.id) ?? [];

/** The ticks in the workspace's own order, nothing that is not listed. */
function ordered(
  workspaces: readonly HiggsfieldWorkspace[],
  wsId: string,
  ids: Iterable<string>,
): PickerState {
  const want = new Set(ids);
  const selected = listed(workspaces, wsId).filter((id) => want.has(id));
  return selected.length ? { workspaceId: wsId, selected } : EMPTY_PICK;
}

/** Tick or untick one project. A project in another workspace than the ticked one changes nothing. */
export function togglePick(
  s: PickerState,
  workspaces: readonly HiggsfieldWorkspace[],
  wsId: string,
  projectId: string,
): PickerState {
  if (s.workspaceId && s.workspaceId !== wsId) return s;
  if (!listed(workspaces, wsId).includes(projectId)) return s;
  const next = new Set(s.selected);
  if (next.has(projectId)) next.delete(projectId);
  else next.add(projectId);
  return ordered(workspaces, wsId, next);
}

/** The group's own checkbox: all of its projects when not all are ticked, else none. */
export function toggleGroup(
  s: PickerState,
  workspaces: readonly HiggsfieldWorkspace[],
  wsId: string,
): PickerState {
  if (s.workspaceId && s.workspaceId !== wsId) return s;
  const all = listed(workspaces, wsId);
  if (all.length === 0) return s;
  return s.selected.length === all.length ? EMPTY_PICK : ordered(workspaces, wsId, all);
}

export function groupState(s: PickerState, ws: HiggsfieldWorkspace): GroupState {
  if (s.workspaceId && s.workspaceId !== ws.id) return "waiting";
  if (s.workspaceId !== ws.id || s.selected.length === 0) return "none";
  return s.selected.length === ws.projects.length ? "all" : "some";
}

/** After the list was read again: keep the ticks that are still there. */
export function prunePick(s: PickerState, workspaces: readonly HiggsfieldWorkspace[]): PickerState {
  return s.workspaceId ? ordered(workspaces, s.workspaceId, s.selected) : EMPTY_PICK;
}

export interface PickSummary {
  count: number;
  /** the primary button: "Arrange 3 projects" */
  label: string;
  /** why the button is off, in words; null when it is on */
  reason: string | null;
  /** what to stage; null while nothing is ticked */
  body: { sourceWorkspaceId: string; projectIds: string[] } | null;
}

export function pickSummary(s: PickerState, workspaces: readonly HiggsfieldWorkspace[]): PickSummary {
  const p = prunePick(s, workspaces);
  const count = p.selected.length;
  const label = count === 0 ? "Arrange projects" : `Arrange ${count} ${count === 1 ? "project" : "projects"}`;
  if (!p.workspaceId || count === 0)
    return { count: 0, label, reason: "Tick the projects to bring in.", body: null };
  return {
    count,
    label,
    reason: null,
    body: { sourceWorkspaceId: p.workspaceId, projectIds: [...p.selected] },
  };
}
